import React, { useState, useEffect, useCallback } from "react";
import { CreditCard, Check, ExternalLink, X, Edit3, Save, XCircle, ArrowLeft, ChevronRight, Loader2, Unlink, RefreshCw, CalendarDays, Mail, Trash2, Search } from "lucide-react";
import { disconnectTwilio } from "../../lib/twilio";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import GoogleBusinessWorkspace from "../../components/GoogleBusinessWorkspace";
import SocialConnectButton, {
  socialConnectDescription,
  type ConnectSocialPlatform,
} from "../../components/SocialConnectButton";
import TwilioSettings from "../../components/settings/TwilioSettings";
import NexusPhoneNumberModal from "../../components/settings/NexusPhoneNumberModal";
import { fetchLiveNexusNumber, formatNexusPhone, type NexusPhoneNumber } from "../../lib/nexusPhone";
import TapToPaySetup from "../../components/TapToPaySetup";
import toast from "react-hot-toast";
// NOTE: Shopify Product Sync is temporarily paused — UI entry points removed from
// the Integrations page. The ShopifySync component, edge functions, and migrations
// remain in the codebase so it can be re-enabled later by restoring these references.
import { useSupabase } from "../../contexts/SupabaseContext";
import { useBusiness } from "../../contexts/BusinessContext";
import { getSiteUrl } from "../../lib/siteConfig";
import { GBP_ENABLED, SOCIAL_ENABLED } from "../../lib/featureFlags";
import { PLATFORM_ICON_SRC } from '../../components/PlatformIcon';
import { isNativeApp, NATIVE_APP_URL_SCHEME } from '../../lib/nativeAppScheme';
import MeetingScreensPanel from '../../components/settings/MeetingScreensPanel';

// Function to configure webhooks directly via Twilio API
async function configureWebhooksDirectly(accountSid: string, authToken: string, phoneNumber: string) {
  try {
    // Format phone number to E.164
    const formatToE164 = (phone: string) => {
      let cleaned = phone.replace(/\D/g, '');
      if (!cleaned.startsWith('1') && cleaned.length === 10) {
        cleaned = '1' + cleaned;
      }
      return cleaned.startsWith('+') ? cleaned : '+' + cleaned;
    };

    const formattedPhone = formatToE164(phoneNumber);
    const credentials = btoa(`${accountSid}:${authToken}`);
    
    // First, verify credentials by checking account info
    const accountResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}.json`,
      {
        headers: {
          'Authorization': `Basic ${credentials}`
        }
      }
    );

    if (!accountResponse.ok) {
      const errorData = await accountResponse.json().catch(() => ({}));
      if (accountResponse.status === 401 || accountResponse.status === 403) {
        throw new Error('Invalid Twilio Account SID or Auth Token. Please verify your credentials.');
      }
      throw new Error(`Failed to authenticate with Twilio: ${errorData.message || 'Unknown error'}`);
    }
    
    // Try to find the phone number - try both with and without + prefix
    let listResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers.json?PhoneNumber=${encodeURIComponent(formattedPhone)}`,
      {
        headers: {
          'Authorization': `Basic ${credentials}`
        }
      }
    );

    if (!listResponse.ok) {
      const errorData = await listResponse.json().catch(() => ({}));
      throw new Error(`Failed to search for phone number: ${errorData.message || 'Unknown error'}`);
    }

    let listData = await listResponse.json();
    
    // If not found with + prefix, try without
    if (!listData.incoming_phone_numbers || listData.incoming_phone_numbers.length === 0) {
      const phoneWithoutPlus = formattedPhone.replace('+', '');
      listResponse = await fetch(
        `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers.json?PhoneNumber=${encodeURIComponent(phoneWithoutPlus)}`,
        {
          headers: {
            'Authorization': `Basic ${credentials}`
          }
        }
      );
      
      if (listResponse.ok) {
        listData = await listResponse.json();
      }
    }

    // If still not found, list all phone numbers to help debug
    if (!listData.incoming_phone_numbers || listData.incoming_phone_numbers.length === 0) {
      // Get all phone numbers to help user identify the issue
      const allNumbersResponse = await fetch(
        `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers.json`,
        {
          headers: {
            'Authorization': `Basic ${credentials}`
          }
        }
      );
      
      let allNumbersData: any = { incoming_phone_numbers: [] };
      if (allNumbersResponse.ok) {
        allNumbersData = await allNumbersResponse.json();
      }
      
      const availableNumbers = allNumbersData.incoming_phone_numbers?.map((num: any) => num.phone_number).join(', ') || 'none';
      throw new Error(
        `Phone number ${formattedPhone} not found in your Twilio account.\n\n` +
        `Available phone numbers in this account: ${availableNumbers}\n\n` +
        `Please ensure:\n` +
        `1. The phone number exists in your Twilio account\n` +
        `2. The phone number format is correct (e.g., +1234567890)\n` +
        `3. You're using the correct Twilio account (Account SID)`
      );
    }

    const phoneNumberSid = listData.incoming_phone_numbers[0].sid;
    
    // Configure the webhooks
    const baseUrl = 'https://idvgoctfaarxiryxprpy.supabase.co/functions/v1';
    const voiceUrl = `${baseUrl}/twilio-call-webhook`;
    const statusCallbackUrl = `${baseUrl}/twilio-call-status`;
    const smsUrl = `${baseUrl}/sms-webhook`;

    const updateResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers/${phoneNumberSid}.json`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          VoiceUrl: voiceUrl,
          VoiceMethod: 'POST',
          StatusCallback: statusCallbackUrl,
          StatusCallbackMethod: 'POST',
          SmsUrl: smsUrl,
          SmsMethod: 'POST'
        })
      }
    );

    if (!updateResponse.ok) {
      const errorData = await updateResponse.json().catch(() => ({}));
      throw new Error(`Failed to configure webhooks: ${errorData.message || 'Unknown error'}`);
    }

    console.log('✅ Webhook URLs configured:', { voiceUrl, statusCallbackUrl, smsUrl });

    // Create TwiML Application for Voice SDK
    console.log('🔍 Creating TwiML Application for Voice SDK...');
    const twimlAppResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/Applications.json`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          FriendlyName: 'Voice SDK Application (Auto-created)',
          VoiceUrl: `${baseUrl}/twilio-voice-handler`,
          VoiceMethod: 'POST',
          StatusCallback: statusCallbackUrl,
          StatusCallbackMethod: 'POST'
        })
      }
    );

    let applicationSid = null;
    if (twimlAppResponse.ok) {
      const appData = await twimlAppResponse.json();
      applicationSid = appData.sid;
      console.log('✅ TwiML Application created:', applicationSid);
    } else {
      console.warn('⚠️ Failed to create TwiML Application, Voice SDK may not work');
    }

    return {
      success: true,
      voiceUrl,
      statusCallbackUrl,
      applicationSid
    };
  } catch (error: any) {
    console.error('Error configuring webhooks:', error);
    throw error;
  }
}

interface GmailConnection {
  id: string;
  email: string;
  connected_at: string;
  is_active: boolean;
  needs_reauth?: boolean;
}

// Helper component for iOS-style modal header
const ModalHeader = ({ title, subtitle, onClose, icon: Icon, iconBgClass, iconColorClass, iconImageSrc }: { title: string; subtitle?: string; onClose: () => void; icon?: React.ElementType; iconBgClass?: string; iconColorClass?: string; iconImageSrc?: string }) => (
  <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60 bg-white">
    <div className="flex items-center gap-2 sm:gap-3">
      {iconImageSrc ? (
        <img src={iconImageSrc} alt="" className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl shadow-sm flex-shrink-0 object-contain" draggable={false} />
      ) : Icon ? (
        <div className={`w-8 h-8 sm:w-9 sm:h-9 ${iconBgClass || 'bg-blue-100'} rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-4 w-4 ${iconColorClass || 'text-blue-600'}`} />
        </div>
      ) : null}
      <div>
        <h2 className="text-base sm:text-xl font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <button
      onClick={onClose}
      className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <X className="h-5 w-5" />
    </button>
  </div>
);

// Gmail Settings Modal (iOS Style)
const GmailSettingsModal = ({ isOpen, onClose, gmailConnected, gmailConnections, handleConnectGmail, handleDisconnectGmail, isConnecting }: { isOpen: boolean; onClose: () => void; gmailConnected: boolean; gmailConnections: GmailConnection[]; handleConnectGmail: () => void; handleDisconnectGmail: (connectionId: string) => void; isConnecting: boolean; }) => {
  if (!isOpen) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 transition-opacity duration-200" style={{ zIndex: 100002, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', opacity: 1, pointerEvents: 'auto' }} onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none sm:items-center" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}
        >
          <ModalHeader title="Gmail Integration" subtitle="Connect your Gmail account for email communication" onClose={onClose} iconImageSrc="/Gmailicon.png" />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {gmailConnected && gmailConnections.length > 0 ? (
                <div className="p-4 sm:p-5 space-y-4">
                  <p className="text-sm text-gray-600">Your Gmail account(s) are connected:</p>
                  {gmailConnections.map((connection) => (
                    <div key={connection.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-900">{connection.email}</span>
                      <button
                        onClick={() => handleDisconnectGmail(connection.id)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        {connection.needs_reauth ? 'Disconnect and Re-authenticate' : 'Disconnect'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 sm:p-5 text-center">
                  <p className="text-sm text-gray-600 mb-4">No Gmail account connected. Connect now to send and receive emails directly from the app.</p>
                  <button
                    onClick={handleConnectGmail}
                    disabled={isConnecting}
                    className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isConnecting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Connecting...
                      </>
                    ) : (
                      <>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Connect Gmail
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>, document.body
  );
};

// Twilio Settings Modal (iOS Style) - Keeping existing implementation but simplified structure
const TwilioSettingsModal = ({ isOpen, onClose, twilioSettings, isSaving, isConfiguringWebhooks, isDisconnecting, startEditing, cancelEditing, saveTwilioField, handleActivateTwilio, handleDisconnectTwilio, maskToken, tempAccountSid, setTempAccountSid, tempAuthToken, setTempAuthToken, tempPhoneNumber, setTempPhoneNumber, tempCallForwardingEnabled, setTempCallForwardingEnabled, tempForwardingNumber, setTempForwardingNumber, editingAccountSid, editingAuthToken, editingPhoneNumber, editingCallForwarding }: any) => {
  if (!isOpen) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 transition-opacity duration-200" style={{ zIndex: 100002, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', opacity: 1, pointerEvents: 'auto' }} onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none sm:items-center" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}
        >
          <ModalHeader title="Twilio Integration" subtitle="Configure SMS and voice communication settings" onClose={onClose} iconImageSrc="/twilio.png" />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-4 sm:p-5 space-y-4 sm:space-y-5">
              {twilioSettings ? (
                <div className="space-y-4">
                  {/* Account SID */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-600">Account SID</span>
                      <div className="flex items-center space-x-2">
                        {!editingAccountSid && twilioSettings.twilio_account_sid && <Check className="h-4 w-4 text-green-500" />}
                        <button
                          onClick={() => editingAccountSid ? cancelEditing('accountSid') : startEditing('accountSid')}
                          className="text-gray-400 hover:text-gray-600"
                          disabled={isSaving}
                        >
                          {editingAccountSid ? <XCircle className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                        </button>
                        {editingAccountSid && (
                          <button
                            onClick={() => saveTwilioField('accountSid')}
                            className="text-green-600 hover:text-green-700"
                            disabled={isSaving}
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {editingAccountSid ? (
                      <input
                        type="text"
                        value={tempAccountSid}
                        onChange={(e) => setTempAccountSid(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="w-full text-sm font-mono text-gray-900 bg-white border border-gray-300 rounded px-3 py-2"
                        placeholder="Enter Account SID"
                      />
                    ) : (
                      <span className={`text-sm font-mono ${twilioSettings.twilio_account_sid ? 'text-gray-900' : 'text-gray-400'}`}>
                        {twilioSettings.twilio_account_sid ? maskToken(twilioSettings.twilio_account_sid) : 'Not configured - click edit to add'}
                      </span>
                    )}
                  </div>

                  {/* Auth Token */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-600">Auth Token</span>
                      <div className="flex items-center space-x-2">
                        {!editingAuthToken && twilioSettings.twilio_auth_token && <Check className="h-4 w-4 text-green-500" />}
                        <button
                          onClick={() => editingAuthToken ? cancelEditing('authToken') : startEditing('authToken')}
                          className="text-gray-400 hover:text-gray-600"
                          disabled={isSaving}
                        >
                          {editingAuthToken ? <XCircle className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                        </button>
                        {editingAuthToken && (
                          <button
                            onClick={() => saveTwilioField('authToken')}
                            className="text-green-600 hover:text-green-700"
                            disabled={isSaving}
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {editingAuthToken ? (
                      <input
                        type="password"
                        value={tempAuthToken}
                        onChange={(e) => setTempAuthToken(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="w-full text-sm font-mono text-gray-900 bg-white border border-gray-300 rounded px-3 py-2"
                        placeholder="Enter Auth Token"
                      />
                    ) : (
                      <span className={`text-sm font-mono ${twilioSettings.twilio_auth_token ? 'text-gray-900' : 'text-gray-400'}`}>
                        {twilioSettings.twilio_auth_token ? maskToken(twilioSettings.twilio_auth_token) : 'Not configured - click edit to add'}
                      </span>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-600">Phone Number</span>
                      <div className="flex items-center space-x-2">
                        {!editingPhoneNumber && twilioSettings.VITE_TWILIO_PHONE_NUMBER && <Check className="h-4 w-4 text-green-500" />}
                        <button
                          onClick={() => editingPhoneNumber ? cancelEditing('phoneNumber') : startEditing('phoneNumber')}
                          className="text-gray-400 hover:text-gray-600"
                          disabled={isSaving}
                        >
                          {editingPhoneNumber ? <XCircle className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                        </button>
                        {editingPhoneNumber && (
                          <button
                            onClick={() => saveTwilioField('phoneNumber')}
                            className="text-green-600 hover:text-green-700"
                            disabled={isSaving}
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {editingPhoneNumber ? (
                      <input
                        type="text"
                        value={tempPhoneNumber}
                        onChange={(e) => setTempPhoneNumber(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="w-full text-sm font-mono text-gray-900 bg-white border border-gray-300 rounded px-3 py-2"
                        placeholder="Enter Phone Number (+1234567890)"
                      />
                    ) : (
                      <span className={`text-sm font-mono ${twilioSettings.VITE_TWILIO_PHONE_NUMBER ? 'text-gray-900' : 'text-gray-400'}`}>
                        {twilioSettings.VITE_TWILIO_PHONE_NUMBER || 'Not configured - click edit to add'}
                      </span>
                    )}
                  </div>

                  {/* Call Forwarding */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-600">Call Forwarding</span>
                      <div className="flex items-center space-x-2">
                        {!editingCallForwarding && (twilioSettings.twilio_call_forwarding_enabled || twilioSettings.twilio_forwarding_number) && <Check className="h-4 w-4 text-green-500" />}
                        <button
                          onClick={() => editingCallForwarding ? cancelEditing('callForwarding') : startEditing('callForwarding')}
                          className="text-gray-400 hover:text-gray-600"
                          disabled={isSaving}
                        >
                          {editingCallForwarding ? <XCircle className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                        </button>
                        {editingCallForwarding && (
                          <button
                            onClick={() => saveTwilioField('callForwarding')}
                            className="text-green-600 hover:text-green-700"
                            disabled={isSaving}
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {editingCallForwarding ? (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={tempCallForwardingEnabled}
                            onChange={(e) => setTempCallForwardingEnabled(e.target.checked)}
                            className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">Enable call forwarding</span>
                        </div>
                        {tempCallForwardingEnabled && (
                          <input
                            type="text"
                            value={tempForwardingNumber}
                            onChange={(e) => setTempForwardingNumber(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            className="w-full text-sm font-mono text-gray-900 bg-white border border-gray-300 rounded px-3 py-2"
                            placeholder="Enter forwarding number (+1234567890)"
                          />
                        )}
                      </div>
                    ) : (
                      <div>
                        <span className={`text-sm font-medium ${twilioSettings.twilio_call_forwarding_enabled ? 'text-green-600' : 'text-gray-500'}`}>
                          {twilioSettings.twilio_call_forwarding_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        {twilioSettings.twilio_call_forwarding_enabled && twilioSettings.twilio_forwarding_number && (
                          <div className="text-sm font-mono text-gray-900 mt-1">{twilioSettings.twilio_forwarding_number}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Activate/Disconnect/Reconnect Buttons */}
                  <div className="pt-4 space-y-2">
                    {!twilioSettings.twilio_enabled && (
                      <button
                        onClick={handleActivateTwilio}
                        disabled={isSaving || isConfiguringWebhooks || isDisconnecting || !twilioSettings.twilio_account_sid || !twilioSettings.twilio_auth_token || !twilioSettings.VITE_TWILIO_PHONE_NUMBER}
                        className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSaving || isConfiguringWebhooks ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            <span>{isConfiguringWebhooks ? 'Configuring Webhooks...' : 'Activating...'}</span>
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            <span>Activate Integration</span>
                          </>
                        )}
                      </button>
                    )}
                    {twilioSettings.twilio_enabled && (
                      <>
                        <button
                          onClick={handleActivateTwilio}
                          disabled={isSaving || isConfiguringWebhooks || isDisconnecting || !twilioSettings.twilio_account_sid || !twilioSettings.twilio_auth_token || !twilioSettings.VITE_TWILIO_PHONE_NUMBER}
                          className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSaving || isConfiguringWebhooks ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              <span>{isConfiguringWebhooks ? 'Reconfiguring Webhooks...' : 'Reconnecting...'}</span>
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              <span>Reconnect / Reconfigure</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleDisconnectTwilio}
                          disabled={isSaving || isConfiguringWebhooks || isDisconnecting}
                          className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isDisconnecting ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              <span>Disconnecting...</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 mr-2" />
                              <span>Disconnect Twilio</span>
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p className="text-sm">Twilio not configured. Enter your credentials above to activate.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>, document.body
  );
};

// Stripe Settings Modal (iOS Style)
const StripeSettingsModal = ({ isOpen, onClose, connectStatus, loading, onConnect, onDisconnect, onRefreshStatus, businessId, supabase }: {
  isOpen: boolean;
  onClose: () => void;
  connectStatus: { isConnected: boolean; accountId?: string; onboardingCompleted?: boolean; chargesEnabled?: boolean };
  loading: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onRefreshStatus: () => void;
  businessId: string | null;
  supabase: any;
}) => {
  const [surchargeEnabled, setSurchargeEnabled] = useState(false);
  const [surchargePercentage, setSurchargePercentage] = useState('2.4');
  const [tipsEnabled, setTipsEnabled] = useState(false);
  const [tipsGoToResource, setTipsGoToResource] = useState(false);
  const [surchargeLoading, setSurchargeLoading] = useState(false);
  const [surchargeSaving, setSurchargeSaving] = useState(false);
  const [surchargeSaved, setSurchargeSaved] = useState(false);

  useEffect(() => {
    if (!isOpen || !businessId) return;
    const loadSurcharge = async () => {
      setSurchargeLoading(true);
      const { data } = await supabase
        .from('businesses')
        .select('credit_card_surcharge_enabled, credit_card_surcharge_percentage, tips_enabled, tips_go_to_resource')
        .eq('id', businessId)
        .single();
      if (data) {
        setSurchargeEnabled(data.credit_card_surcharge_enabled ?? false);
        setSurchargePercentage(String(data.credit_card_surcharge_percentage ?? '0'));
        setTipsEnabled(data.tips_enabled ?? false);
        setTipsGoToResource(data.tips_go_to_resource ?? false);
      }
      setSurchargeLoading(false);
    };
    loadSurcharge();
  }, [isOpen, businessId, supabase]);

  const handleSaveSurcharge = async () => {
    if (!businessId) return;
    setSurchargeSaving(true);
    const pct = Math.min(3, Math.max(0, parseFloat(surchargePercentage) || 0));
    await supabase
      .from('businesses')
      .update({
        credit_card_surcharge_enabled: surchargeEnabled,
        credit_card_surcharge_percentage: pct,
        tips_enabled: tipsEnabled,
        tips_go_to_resource: tipsGoToResource,
      })
      .eq('id', businessId);
    setSurchargePercentage(String(pct));
    setSurchargeSaving(false);
    setSurchargeSaved(true);
    setTimeout(() => setSurchargeSaved(false), 2000);
  };

  if (!isOpen) return null;

  const fullyConnected = connectStatus.isConnected && connectStatus.onboardingCompleted && connectStatus.chargesEnabled;
  const needsOnboarding = connectStatus.isConnected && !connectStatus.onboardingCompleted;

  return createPortal(
    <>
      <div className="fixed inset-0 transition-opacity duration-200" style={{ zIndex: 100002, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', opacity: 1, pointerEvents: 'auto' }} onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none sm:items-center" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}
        >
          <ModalHeader title="Stripe Integration" subtitle="Payment processing and invoicing" onClose={onClose} iconImageSrc="/stripeicon.png" />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {fullyConnected ? (
                <div className="p-4 sm:p-5 space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-800">Stripe is connected</p>
                      <p className="text-xs text-green-600 truncate">Account: {connectStatus.accountId}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => window.open(`https://dashboard.stripe.com/connect/accounts/${connectStatus.accountId}`, '_blank')}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Stripe Dashboard
                  </button>

                  <button
                    onClick={onRefreshStatus}
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Refresh Status
                  </button>

                  <button
                    onClick={onDisconnect}
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlink className="h-4 w-4 mr-2" />}
                    Disconnect Stripe
                  </button>

                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Card Processing Fee</h3>
                    <p className="text-xs text-gray-500 mb-3">
                      When enabled, a processing fee is added to credit card payments processed in-person. Debit cards are not surcharged.
                    </p>

                    {surchargeLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label htmlFor="surcharge-toggle" className="text-sm text-gray-700">Enable credit card fee</label>
                          <button
                            id="surcharge-toggle"
                            type="button"
                            role="switch"
                            aria-checked={surchargeEnabled}
                            onClick={() => setSurchargeEnabled(!surchargeEnabled)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${surchargeEnabled ? 'bg-purple-600' : 'bg-gray-200'}`}
                          >
                            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${surchargeEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                          </button>
                        </div>

                        {surchargeEnabled && (
                          <div>
                            <label htmlFor="surcharge-pct" className="block text-sm text-gray-700 mb-1">Fee percentage (max 3%)</label>
                            <div className="flex items-center gap-2">
                              <input
                                id="surcharge-pct"
                                type="number"
                                min="0"
                                max="3"
                                step="0.1"
                                value={surchargePercentage}
                                onChange={(e) => setSurchargePercentage(e.target.value)}
                                className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              />
                              <span className="text-sm text-gray-500">%</span>
                            </div>
                          </div>
                        )}

                        <div className="border-t border-gray-200 pt-4 mt-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-1">Tips</h3>
                          <p className="text-xs text-gray-500 mb-3">
                            When enabled, customers will see tip options when paying via payment links.
                          </p>
                          <div className="flex items-center justify-between">
                            <label htmlFor="tips-toggle" className="text-sm text-gray-700">Enable tipping</label>
                            <button
                              id="tips-toggle"
                              type="button"
                              role="switch"
                              aria-checked={tipsEnabled}
                              onClick={() => setTipsEnabled(!tipsEnabled)}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${tipsEnabled ? 'bg-purple-600' : 'bg-gray-200'}`}
                            >
                              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${tipsEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                            </button>
                          </div>

                          {tipsEnabled && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                              <p className="text-xs font-medium text-gray-600 mb-2">Tips go to</p>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => setTipsGoToResource(false)}
                                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                    !tipsGoToResource
                                      ? 'bg-purple-600 text-white border-purple-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Business
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setTipsGoToResource(true)}
                                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                    tipsGoToResource
                                      ? 'bg-purple-600 text-white border-purple-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Resource
                                </button>
                              </div>
                              <p className="text-xs text-gray-400">
                                {tipsGoToResource
                                  ? 'Tips will be assigned to the resource on the booking.'
                                  : 'Tips will go to the business.'}
                              </p>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={handleSaveSurcharge}
                          disabled={surchargeSaving}
                          className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {surchargeSaving ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                          ) : surchargeSaved ? (
                            <><Check className="h-4 w-4 mr-2" />Saved</>
                          ) : (
                            <><Save className="h-4 w-4 mr-2" />Save Settings</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <TapToPaySetup businessId={businessId} />
                  </div>
                </div>
              ) : needsOnboarding ? (
                <div className="p-4 sm:p-5 space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <CreditCard className="h-4 w-4 text-orange-600" />
                    </div>
                    <p className="text-sm font-medium text-orange-800">Stripe setup is incomplete</p>
                  </div>

                  <button
                    onClick={onConnect}
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                    Complete Stripe Setup
                  </button>

                  <button
                    onClick={onDisconnect}
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlink className="h-4 w-4 mr-2" />}
                    Disconnect Stripe
                  </button>
                </div>
              ) : (
                <div className="p-4 sm:p-5 text-center">
                  <p className="text-sm text-gray-600 mb-4">Connect your Stripe account to accept payments directly from the app.</p>
                  <button
                    onClick={onConnect}
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                    Connect Stripe
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>, document.body
  );
};

// Google Calendar Settings Modal (iOS Style)
const GcalSettingsModal = ({ isOpen, onClose, connected, needsReauth, email, onConnect, onDisconnect, isConnecting, isDisconnecting, businessId, calendarSyncId, businessSlug, businessName, timezone, businessHours, logoUrl, supabase }: {
  isOpen: boolean; onClose: () => void; connected: boolean; needsReauth: boolean; email: string | null;
  onConnect: () => void; onDisconnect: () => void; isConnecting: boolean; isDisconnecting: boolean;
  businessId: string | null; calendarSyncId: string | null; businessSlug: string; businessName: string;
  timezone: string; businessHours?: Record<string, { enabled?: boolean; closed?: boolean; open?: string; close?: string }>;
  logoUrl?: string | null;
  supabase: { from: (table: string) => any };
}) => {
  if (!isOpen) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 transition-opacity duration-200" style={{ zIndex: 100002, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }} onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <ModalHeader title="Google Calendar" subtitle="Sync bookings and share a branded meeting page" onClose={onClose} iconImageSrc="/Gcalnder.png" />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {connected ? (
                <div className="p-4 sm:p-5 space-y-4">
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${needsReauth ? 'bg-amber-50' : 'bg-green-50'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${needsReauth ? 'bg-amber-100' : 'bg-green-100'}`}>
                      {needsReauth ? <RefreshCw className="h-4 w-4 text-amber-600" /> : <Check className="h-4 w-4 text-green-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${needsReauth ? 'text-amber-800' : 'text-green-800'}`}>
                        {needsReauth ? 'Google Calendar needs to be reconnected' : 'Google Calendar is connected'}
                      </p>
                      {email && <p className={`text-xs truncate ${needsReauth ? 'text-amber-600' : 'text-green-600'}`}>{email}</p>}
                      {needsReauth && (
                        <p className="text-xs text-amber-700 mt-1">Google stopped accepting the saved sign-in, so meeting pages cannot see open times until you connect again.</p>
                      )}
                    </div>
                  </div>
                  {needsReauth && (
                    <button
                      onClick={onConnect}
                      disabled={isConnecting}
                      className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Reconnect Google Calendar
                    </button>
                  )}
                  <button
                    onClick={onDisconnect}
                    disabled={isDisconnecting}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isDisconnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlink className="h-4 w-4 mr-2" />}
                    Disconnect Google Calendar
                  </button>
                </div>
              ) : (
                <div className="p-4 sm:p-5 text-center">
                  <p className="text-sm text-gray-600 mb-4">Connect your Google Calendar to automatically sync bookings both ways.</p>
                  <button
                    onClick={onConnect}
                    disabled={isConnecting}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarDays className="h-4 w-4 mr-2" />}
                    Connect Google Calendar
                  </button>
                </div>
              )}
            </div>
            {connected && businessId && (
              <MeetingScreensPanel
                supabase={supabase}
                businessId={businessId}
                calendarSyncId={calendarSyncId}
                businessSlug={businessSlug}
                businessName={businessName}
                timezone={timezone}
                businessHours={businessHours}
                logoUrl={logoUrl}
              />
            )}
          </div>
        </div>
      </div>
    </>, document.body
  );
};

// Google Drive Settings Modal (iOS Style). The bots use this connection.
const GdriveSettingsModal = ({ isOpen, onClose, connected, email, needsReauth, error, onConnect, onDisconnect, isConnecting, isDisconnecting }: {
  isOpen: boolean; onClose: () => void; connected: boolean; email: string | null; needsReauth: boolean; error: string | null;
  onConnect: () => void; onDisconnect: () => void; isConnecting: boolean; isDisconnecting: boolean;
}) => {
  if (!isOpen) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 transition-opacity duration-200" style={{ zIndex: 100002, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }} onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <ModalHeader title="Google Drive" subtitle="Let your AI team work with your Drive files" onClose={onClose} iconImageSrc="/google-drive.svg" />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {connected ? (
                <div className="p-4 sm:p-5 space-y-4">
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${needsReauth ? 'bg-amber-50' : 'bg-green-50'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${needsReauth ? 'bg-amber-100' : 'bg-green-100'}`}>
                      {needsReauth ? <RefreshCw className="h-4 w-4 text-amber-600" /> : <Check className="h-4 w-4 text-green-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${needsReauth ? 'text-amber-800' : 'text-green-800'}`}>
                        {needsReauth ? 'Google Drive needs to be reconnected' : 'Google Drive is connected'}
                      </p>
                      {email && <p className={`text-xs truncate ${needsReauth ? 'text-amber-600' : 'text-green-600'}`}>{email}</p>}
                    </div>
                  </div>
                  {needsReauth && (
                    <button
                      onClick={onConnect}
                      disabled={isConnecting}
                      className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Reconnect Google Drive
                    </button>
                  )}
                  <button
                    onClick={onDisconnect}
                    disabled={isDisconnecting}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isDisconnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlink className="h-4 w-4 mr-2" />}
                    Disconnect Google Drive
                  </button>
                </div>
              ) : (
                <div className="p-4 sm:p-5 text-center">
                  <p className="text-sm text-gray-600 mb-4">Connect a Google account so your AI team can find and use the files in its Drive.</p>
                  {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
                  <button
                    onClick={onConnect}
                    disabled={isConnecting}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <img src="/google-drive.svg" alt="" className="h-4 w-4 mr-2" draggable={false} />}
                    Connect Google Drive
                  </button>
                </div>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
              <p className="text-sm font-medium text-gray-900 mb-2">What your AI team can do with it</p>
              <ul className="text-sm text-gray-600 space-y-1.5 list-disc pl-5">
                <li>Search Drive by title or by words inside a document, browse folders, and read Google Docs, Sheets, and Slides.</li>
                <li>Bring a Drive file into the team library to email it, text it, put it on the website, or work on it.</li>
                <li>Upload files it makes, share files, create folders, rename, move, or trash, each with an Approve card in the chat first.</li>
              </ul>
              <p className="text-xs text-gray-500 mt-3">Bots never act on Drive without a tap from you unless you press Always allow on a card.</p>
            </div>
          </div>
        </div>
      </div>
    </>, document.body
  );
};

const SearchConsoleSettingsModal = ({
  isOpen, onClose, connected, email, lastSyncedAt, syncError, needsReauth, onConnect, onDisconnect, isConnecting, isDisconnecting,
}: {
  isOpen: boolean; onClose: () => void; connected: boolean; email: string | null;
  lastSyncedAt: string | null; syncError: string | null; needsReauth: boolean;
  onConnect: () => void; onDisconnect: () => void; isConnecting: boolean; isDisconnecting: boolean;
}) => {
  if (!isOpen) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 transition-opacity duration-200" style={{ zIndex: 100002, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }} onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <ModalHeader title="Search Console" subtitle="Sync search performance into Nexus" onClose={onClose} iconImageSrc="/search-console.svg" />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {connected ? (
                <div className="p-4 sm:p-5 space-y-4">
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${needsReauth ? 'bg-amber-50' : 'bg-green-50'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${needsReauth ? 'bg-amber-100' : 'bg-green-100'}`}>
                      <Check className={`h-4 w-4 ${needsReauth ? 'text-amber-600' : 'text-green-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${needsReauth ? 'text-amber-800' : 'text-green-800'}`}>
                        {needsReauth ? 'Search Console needs to be reconnected' : 'Search Console is connected'}
                      </p>
                      {email && <p className={`text-xs truncate ${needsReauth ? 'text-amber-700' : 'text-green-600'}`}>{email}</p>}
                      {!needsReauth && (
                        <p className="text-xs text-green-700 mt-1">
                          {lastSyncedAt ? `Last synced ${new Date(lastSyncedAt).toLocaleString()}` : 'Sync starts as soon as Google finishes connecting.'}
                        </p>
                      )}
                    </div>
                  </div>
                  {syncError && <p className="text-xs text-amber-700">{syncError}</p>}
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Nexus stores the properties, sitemaps, and search performance for this Google account. A report view is not on this screen yet.
                  </p>
                  {needsReauth ? (
                    <button
                      onClick={onConnect}
                      disabled={isConnecting}
                      className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                      Reconnect Search Console
                    </button>
                  ) : (
                    <button
                      onClick={onDisconnect}
                      disabled={isDisconnecting}
                      className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isDisconnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlink className="h-4 w-4 mr-2" />}
                      Disconnect Search Console
                    </button>
                  )}
                </div>
              ) : (
                <div className="p-4 sm:p-5 text-center">
                  <p className="text-sm text-gray-600 mb-4">Connect Search Console and Nexus will start syncing clicks, impressions, queries, pages, and sitemaps.</p>
                  <button
                    onClick={onConnect}
                    disabled={isConnecting}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                    Connect Search Console
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>, document.body
  );
};

// Google Business Profile Settings Modal — connect / disconnect only (activity lives in Media Manager)
const GbpSettingsModal = ({
  isOpen,
  onClose,
  onConnectionChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConnectionChange?: (connected: boolean) => void;
}) => {
  if (!isOpen) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 transition-opacity duration-200" style={{ zIndex: 100002, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }} onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-lg sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <ModalHeader
            title="Google Business Profile"
            subtitle="Connect your profile for reviews, posts, and photos"
            onClose={onClose}
            iconImageSrc={PLATFORM_ICON_SRC.google_business}
          />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            <GoogleBusinessWorkspace mode="connection" onConnectionChange={onConnectionChange} />
          </div>
        </div>
      </div>
    </>, document.body
  );
};

const SocialSettingsModal = ({
  isOpen,
  platform,
  onClose,
  onConnectionChange,
}: {
  isOpen: boolean;
  platform: ConnectSocialPlatform;
  onClose: () => void;
  onConnectionChange?: () => void;
}) => {
  if (!isOpen) return null;
  const label =
    platform === 'facebook' ? 'Facebook' : platform === 'instagram' ? 'Instagram' : 'LinkedIn';
  return createPortal(
    <>
      <div className="fixed inset-0 transition-opacity duration-200" style={{ zIndex: 100002, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }} onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-lg sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <ModalHeader
            title={label}
            subtitle={
              platform === 'linkedin'
                ? 'Connect a personal profile or company page'
                : `Connect ${label} to publish and automate posts`
            }
            onClose={onClose}
            iconImageSrc={PLATFORM_ICON_SRC[platform]}
          />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 space-y-4">
            <p className="text-sm text-gray-600 leading-relaxed">
              {socialConnectDescription(platform)}
            </p>
            <div className="bg-white border border-gray-200 rounded-2xl p-4">
              <SocialConnectButton
                platform={platform}
                size="large"
                onConnectionChange={onConnectionChange}
              />
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

// SendGrid (verified sender) Settings Modal (iOS Style).
// This is the same verified-sender flow as Marketing → Campaigns settings —
// every email system falls back to SendGrid when no Gmail is connected.
interface SendgridSender { email: string; sendgrid_sender_id: string; verification_status: string }
const SendgridSettingsModal = ({ isOpen, onClose, senders, loading, verifying, checkingId, onAddEmail, onCheckStatus, onRemove }: {
  isOpen: boolean; onClose: () => void; senders: SendgridSender[]; loading: boolean;
  verifying: boolean; checkingId: string | null;
  onAddEmail: (email: string) => Promise<void>;
  onCheckStatus: (sender: SendgridSender) => void;
  onRemove: (sender: SendgridSender) => void;
}) => {
  const [email, setEmail] = useState('');
  if (!isOpen) return null;
  const hasSenders = senders.length > 0;
  return createPortal(
    <>
      <div className="fixed inset-0 transition-opacity duration-200" style={{ zIndex: 100002, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }} onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 100003 }}>
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <ModalHeader title="SendGrid Email" subtitle="Verified sender for email without Gmail" onClose={onClose} iconImageSrc={PLATFORM_ICON_SRC.sendgrid} />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 space-y-4">
            <p className="text-xs text-gray-500 leading-relaxed">
              Verify an email address to send from Nexus without connecting Gmail. Campaigns and all
              other email sending automatically use this address when no Gmail account is connected.
            </p>

            {loading ? (
              <div className="py-10 text-center text-gray-500 text-sm">Loading…</div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {hasSenders ? (
                  <div className="divide-y divide-gray-100">
                    {senders.map((sender) => {
                      const verified = sender.verification_status === 'verified';
                      return (
                        <div key={sender.sendgrid_sender_id || sender.email} className="p-4 sm:p-5">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${verified ? 'bg-green-100' : 'bg-amber-100'}`}>
                              {verified ? <Check className="h-4 w-4 text-green-600" /> : <Loader2 className="h-4 w-4 text-amber-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{sender.email}</p>
                              <p className={`text-xs ${verified ? 'text-green-600' : 'text-amber-600'}`}>
                                {verified ? 'Verified — ready to send' : 'Pending — check your inbox and click the verification link'}
                              </p>
                            </div>
                            {!verified && (
                              <button
                                onClick={() => onCheckStatus(sender)}
                                disabled={checkingId === sender.sendgrid_sender_id}
                                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
                              >
                                {checkingId === sender.sendgrid_sender_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                Check status
                              </button>
                            )}
                            <button
                              onClick={() => onRemove(sender)}
                              className="flex-shrink-0 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove sender"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          {!verified && (
                            <div className="mt-3 ml-11 bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <p className="text-xs text-blue-800 leading-relaxed">
                                SendGrid emailed a verification link to <span className="font-semibold">{sender.email}</span> —
                                click it, then hit Check status here.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 sm:p-5">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Sender email address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@yourbusiness.com"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                    />
                    <p className="text-xs text-gray-500 mb-4">
                      We'll send a verification email to this address from SendGrid. Click the link
                      in that email and you're ready to send — no SendGrid account needed.
                    </p>
                    <button
                      onClick={async () => {
                        await onAddEmail(email.trim());
                        setEmail('');
                      }}
                      disabled={verifying || !email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}
                      className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {verifying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                      Send verification email
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>, document.body
  );
};

function NexusConnectIntegrationCard({
  onClick,
  iconSrc,
  framed,
  title,
  category,
  description,
  status,
}: {
  onClick: () => void;
  iconSrc: string;
  framed?: boolean;
  title: string;
  category: string;
  description: string;
  status: React.ReactNode;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className="group bg-white rounded-xl border border-gray-100 px-2.5 py-2 hover:shadow-sm transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <img
          src={iconSrc}
          alt=""
          draggable={false}
          className={`w-11 h-11 rounded-xl shadow-sm flex-shrink-0 object-contain ${framed ? 'border border-gray-100 bg-white' : ''}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate leading-tight">{title}</h3>
            <span className="text-[11px] text-gray-400 shrink-0 leading-tight">{category}</span>
          </div>
          <p className="text-xs text-gray-500 truncate leading-tight mt-0.5">{description}</p>
          <div className="mt-0.5 leading-tight">{status}</div>
        </div>
      </div>
    </div>
  );
}

function gcalConnectError(reason: string) {
  if (reason === 'scope') return 'Google Calendar access was not allowed. Connect again and accept calendar access.';
  if (reason === 'refresh') return 'Google did not allow Nexus to stay connected. Connect again and accept every permission.';
  if (reason === 'save') return 'Google signed in, but Nexus could not save the calendar connection.';
  return 'Google Calendar could not be connected. Try again.';
}

function IntegrationStatus({ connected, label }: { connected: boolean; label?: string }) {
  if (connected) {
    return (
      <span className="text-xs text-green-600 font-medium flex items-center">
        <Check className="h-3 w-3 mr-1 flex-shrink-0" />
        Connected
      </span>
    );
  }
  return <span className="text-xs font-medium text-blue-600">{label || 'Activate'}</span>;
}

export function Integrations({ variant = 'page' }: { variant?: 'page' | 'nexusConnect' } = {}) {
  const supabase = useSupabase();
  const { businessId, selectedBusiness: business, forceLoadBusinesses } = useBusiness();
  const navigate = useNavigate();
  const [showGmailSettings, setShowGmailSettings] = useState(false);
  const [showTwilioSettings, setShowTwilioSettings] = useState(false);
  const [showNexusPhone, setShowNexusPhone] = useState(false);
  const [nexusNumber, setNexusNumber] = useState<NexusPhoneNumber | null>(null);
  const [showStripeSettings, setShowStripeSettings] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [twilioConnected, setTwilioConnected] = useState(false);
  const [gmailConnections, setGmailConnections] = useState<GmailConnection[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [twilioSettings, setTwilioSettings] = useState<any>(null);

  // Stripe state
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeConnectStatus, setStripeConnectStatus] = useState<{
    isConnected: boolean;
    accountId?: string;
    onboardingCompleted?: boolean;
    chargesEnabled?: boolean;
  }>({ isConnected: false });
  
  // Editing states for Twilio fields
  const [editingAccountSid, setEditingAccountSid] = useState(false);
  const [editingAuthToken, setEditingAuthToken] = useState(false);
  const [editingPhoneNumber, setEditingPhoneNumber] = useState(false);
  const [editingCallForwarding, setEditingCallForwarding] = useState(false);
  const [tempAccountSid, setTempAccountSid] = useState('');
  const [tempAuthToken, setTempAuthToken] = useState('');
  const [tempPhoneNumber, setTempPhoneNumber] = useState('');
  const [tempCallForwardingEnabled, setTempCallForwardingEnabled] = useState(false);
  const [tempForwardingNumber, setTempForwardingNumber] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isConfiguringWebhooks, setIsConfiguringWebhooks] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Google Calendar state
  const [gcalConnected, setGcalConnected] = useState(false);
  const [gcalNeedsReauth, setGcalNeedsReauth] = useState(false);
  const [gcalEmail, setGcalEmail] = useState<string | null>(null);
  const [gcalSyncId, setGcalSyncId] = useState<string | null>(null);
  const [gcalConnecting, setGcalConnecting] = useState(false);
  const [gcalDisconnecting, setGcalDisconnecting] = useState(false);
  const [showGcalSettings, setShowGcalSettings] = useState(false);
  // Google Drive state (the bots' connection)
  const [gdriveConnected, setGdriveConnected] = useState(false);
  const [gdriveEmail, setGdriveEmail] = useState<string | null>(null);
  const [gdriveConnectionId, setGdriveConnectionId] = useState<string | null>(null);
  const [gdriveNeedsReauth, setGdriveNeedsReauth] = useState(false);
  const [gdriveError, setGdriveError] = useState<string | null>(null);
  const [gdriveConnecting, setGdriveConnecting] = useState(false);
  const [gdriveDisconnecting, setGdriveDisconnecting] = useState(false);
  const [showGdriveSettings, setShowGdriveSettings] = useState(false);
  const [gscConnected, setGscConnected] = useState(false);
  const [gscEmail, setGscEmail] = useState<string | null>(null);
  const [gscConnectionId, setGscConnectionId] = useState<string | null>(null);
  const [gscLastSyncedAt, setGscLastSyncedAt] = useState<string | null>(null);
  const [gscSyncError, setGscSyncError] = useState<string | null>(null);
  const [gscNeedsReauth, setGscNeedsReauth] = useState(false);
  const [gscConnecting, setGscConnecting] = useState(false);
  const [gscDisconnecting, setGscDisconnecting] = useState(false);
  const [showGscSettings, setShowGscSettings] = useState(false);
  const [showGbpSettings, setShowGbpSettings] = useState(false);
  const [gbpConnected, setGbpConnected] = useState(false);
  const [showSocialSettings, setShowSocialSettings] = useState<ConnectSocialPlatform | null>(null);
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [instagramConnected, setInstagramConnected] = useState(false);
  const [linkedinConnected, setLinkedinConnected] = useState(false);

  // Open GBP settings after OAuth return (flag / resume stash / URL marker)
  useEffect(() => {
    if (!GBP_ENABLED) return;
    try {
      if (sessionStorage.getItem('gbp_open_settings') === '1') {
        sessionStorage.removeItem('gbp_open_settings');
        setShowGbpSettings(true);
      }
      if (sessionStorage.getItem('gbp_returning') === '1' || sessionStorage.getItem('gbp_oauth_resume')) {
        setShowGbpSettings(true);
      }
    } catch {
      /* ignore */
    }
    const params = new URLSearchParams(window.location.search);
    if (
      params.get('pendingDataToken') ||
      params.get('pending_data_token') ||
      params.get('gbp_error') ||
      params.get('gbp_oauth') === '1' ||
      params.get('gbp_returning') === '1'
    ) {
      setShowGbpSettings(true);
    }
    if (SOCIAL_ENABLED && params.get('social_oauth') === '1') {
      const p = params.get('platform');
      if (p === 'facebook' || p === 'instagram' || p === 'linkedin') setShowSocialSettings(p);
    }
  }, []);

  useEffect(() => {
    if (!SOCIAL_ENABLED || !businessId) return;
    void (async () => {
      const { data } = await supabase
        .from('social_media_connections')
        .select('platform')
        .eq('business_id', businessId)
        .eq('provider', 'ayrshare')
        .eq('is_active', true);
      const platforms = new Set((data || []).map((r) => r.platform as string));
      setFacebookConnected(platforms.has('facebook'));
      setInstagramConnected(platforms.has('instagram'));
      setLinkedinConnected(platforms.has('linkedin'));
    })();
  }, [businessId, showSocialSettings, supabase]);

  // SendGrid verified-sender state (same flow as Marketing → Campaigns settings)
  const [showSendgridSettings, setShowSendgridSettings] = useState(false);
  const [sendgridSenders, setSendgridSenders] = useState<SendgridSender[]>([]);
  const [sendgridLoading, setSendgridLoading] = useState(false);
  const [sendgridVerifying, setSendgridVerifying] = useState(false);
  const [sendgridCheckingId, setSendgridCheckingId] = useState<string | null>(null);
  // Load Twilio settings (+ the Nexus phone number row, which shares the same
  // businesses.twilio_* columns under twilio_connection_method = 'nexus').
  const loadTwilioSettings = useCallback(async () => {
    if (!businessId) return;

    const [{ data, error }, nexusRow] = await Promise.all([
      supabase
        .from('businesses')
        .select('twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER, twilio_enabled, twilio_status, twilio_connection_method, twilio_call_forwarding_enabled, twilio_forwarding_number, twilio_webhooks_configured, twilio_application_sid')
        .eq('id', businessId)
        .single(),
      fetchLiveNexusNumber(businessId),
    ]);

    setNexusNumber(nexusRow);

    if (error) {
      console.error('Error loading Twilio settings:', error);
      return;
    }

    setTwilioSettings(data);

    // Check if Twilio is connected
    if (data?.twilio_account_sid && data?.twilio_auth_token && data?.VITE_TWILIO_PHONE_NUMBER) {
      setTwilioConnected(!!data.twilio_enabled);
    } else {
      setTwilioConnected(false);
    }
  }, [businessId, supabase]);

  useEffect(() => {
    void loadTwilioSettings();
  }, [loadTwilioSettings]);

  // A Nexus number lives in the same columns, so "own Twilio connected" is only
  // true when the method is NOT 'nexus'.
  const usingNexusNumber = twilioSettings?.twilio_connection_method === 'nexus';
  const ownTwilioConnected = twilioConnected && !usingNexusNumber;
  const nexusPhoneActive = !!nexusNumber && nexusNumber.status === 'active';

  // Coming back from a Stripe Checkout for the phone balance → reopen the
  // Nexus Phone modal, which confirms the payment and finishes provisioning.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('nexus_phone_checkout')) setShowNexusPhone(true);
  }, []);

  // Surface the Twilio Connect redirect result (?twilio_connect=...) and open the
  // Twilio modal on success so the user can finish by picking a number.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('twilio_connect');
    if (!result) return;
    if (result === 'success') {
      toast.success('Twilio account connected — now choose a phone number.');
      setShowTwilioSettings(true);
    } else if (result === 'denied') {
      toast.error('Twilio authorization was cancelled.');
    } else if (result === 'expired') {
      toast.error('Connection link expired. Please try connecting again.');
    } else {
      toast.error('Could not complete the Twilio connection.');
    }
    // Strip the param so refreshes don't re-trigger.
    params.delete('twilio_connect');
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, []);

  // Load Gmail connections
  const loadGmailConnections = useCallback(async () => {
      if (!businessId) return;
      
      const { data, error } = await supabase
        .from('gmail_connections')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('connected_at', { ascending: false });

      if (error) {
        console.error('Error loading Gmail connections:', error);
        return;
      }

      setGmailConnections(data || []);
      setGmailConnected((data || []).length > 0);
  }, [businessId, supabase]);

  useEffect(() => {
    loadGmailConnections();
  }, [loadGmailConnections]);

  // Refresh connections when modal opens to ensure fresh data
  useEffect(() => {
    if (showGmailSettings) {
      loadGmailConnections();
    }
  }, [showGmailSettings, loadGmailConnections]);

  useEffect(() => {
    if (!businessId) {
      setGbpConnected(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('google_business_connections')
        .select('id')
        .eq('business_id', businessId)
        .in('provider', ['ayrshare', 'google'])
        .eq('is_active', true)
        .limit(1);
      if (!cancelled) setGbpConnected(!!(data && data.length > 0));
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, showGbpSettings, supabase]);

  const handleConnectGmail = async () => {
    setIsConnecting(true);
    try {
      // Use Vite environment variables
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      // Build redirect URI: https://[project-ref].supabase.co/functions/v1/gmail-oauth-callback
      const supabaseUrlClean = supabaseUrl.replace('/rest/v1', '').replace(/\/$/, '');
      const redirectUrl = `${supabaseUrlClean}/functions/v1/gmail-oauth-callback`;
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      
      console.log('🔗 Gmail OAuth Redirect URI:', redirectUrl);
      console.log('⚠️  This URI must EXACTLY match what is registered in Google Cloud Console!');
      
      if (!clientId) {
        console.error("Google Client ID not configured. Please contact support.");
        setIsConnecting(false);
        return;
      }

      const scope = 'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.email';
      
      // Encode state as base64 JSON to match what callback expects.
      // Return to Nexus Connect's Integrations tab. The tab is a URL param so
      // the OAuth round-trip lands on the current screen.
      const stateData = {
        businessId: businessId,
        returnUrl: isNativeApp()
          ? `${NATIVE_APP_URL_SCHEME}://app-store?tab=integrations`
          : `${window.location.origin}/app-store?tab=integrations`,
      };
      const encodedState = btoa(JSON.stringify(stateData));
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUrl)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent&state=${encodeURIComponent(encodedState)}`;
      
      const popup = window.open(authUrl, '_blank', 'width=500,height=600');
      
      // Poll the popup to check when OAuth is complete
      const checkPopup = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          // Reload Gmail connections when popup closes
          if (!businessId) {
            setIsConnecting(false);
            return;
          }
          
          // Reload connections to get fresh data including needs_reauth flag
          await loadGmailConnections();
          setIsConnecting(false);
        } else {
          // Check if popup has redirected to our domain (OAuth complete)
          try {
            const popupUrl = popup.location.href;
            if (popupUrl.includes(window.location.origin) && popupUrl.includes('success=gmail_connected')) {
              popup.close();
              clearInterval(checkPopup);
              // Reload Gmail connections
              if (!businessId) {
                setIsConnecting(false);
                return;
              }
              
              // Reload connections to get fresh data including needs_reauth flag
              await loadGmailConnections();
              setIsConnecting(false);
            }
          } catch (e) {
            // Cross-origin error is expected while popup is on Google's domain
            // This is normal and we'll keep polling
          }
        }
      }, 500);
      
      // Cleanup interval after 5 minutes (timeout)
      setTimeout(() => {
        clearInterval(checkPopup);
        if (popup && !popup.closed) {
          popup.close();
        }
        setIsConnecting(false);
      }, 300000);
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      setIsConnecting(false);
    }
  };

  const handleDisconnectGmail = async (connectionId: string) => {
    try {
      const { error } = await supabase
        .from('gmail_connections')
        .update({ 
          is_active: false,
          needs_reauth: false // Clear re-auth flag when disconnecting
        })
        .eq('id', connectionId);

      if (error) throw error;

      // Reload connections to get fresh data
      await loadGmailConnections();
    } catch (error) {
      console.error('Error disconnecting Gmail:', error);
    }
  };

  // Google Calendar handlers
  const loadGcalConnection = useCallback(async () => {
    if (!businessId) return;
    const { data, error } = await supabase
      .from('google_calendar_sync')
      .select('id, google_email, is_active, needs_reauth, token_expires_at')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .is('resource_id', null)
      .limit(1)
      .maybeSingle();
    if (!error && data) {
      const expired = data.token_expires_at ? new Date(data.token_expires_at).getTime() < Date.now() - 24 * 60 * 60 * 1000 : false;
      setGcalConnected(true);
      setGcalNeedsReauth(data.needs_reauth === true || expired);
      setGcalEmail(data.google_email);
      setGcalSyncId(data.id);
    } else {
      setGcalConnected(false);
      setGcalNeedsReauth(false);
      setGcalEmail(null);
      setGcalSyncId(null);
    }
  }, [businessId, supabase]);

  useEffect(() => { loadGcalConnection(); }, [loadGcalConnection]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('gcal_error');
    if (!reason && !params.has('gcal')) return;
    if (reason) toast.error(gcalConnectError(reason));
    else if (params.get('gcal') === 'connected') toast.success('Google Calendar connected');
    params.delete('gcal_error');
    params.delete('gcal');
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', next);
  }, []);

  const handleConnectGcal = async () => {
    if (!businessId) return;
    setGcalConnecting(true);
    try {
      const returnUrl = isNativeApp()
        ? `${NATIVE_APP_URL_SCHEME}://app-store?tab=integrations`
        : `${window.location.origin}/app-store?tab=integrations`;
      const { data, error } = await supabase.functions.invoke('gcal-oauth-init', {
        body: { businessId, returnUrl },
      });
      if (error) throw error;
      const authUrl = data?.authUrl;
      if (!authUrl) throw new Error('No auth URL returned');

      if (isNativeApp()) {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: authUrl });
        setGcalConnecting(false);
        return;
      }

      const popup = window.open(authUrl, 'gcal-oauth', 'width=600,height=700,left=200,top=100');
      if (!popup) {
        window.location.assign(authUrl);
        return;
      }
      const checkPopup = setInterval(async () => {
        if (popup.closed) {
          clearInterval(checkPopup);
          await loadGcalConnection();
          setGcalConnecting(false);
          return;
        }
        try {
          const loc = popup.location.href;
          if (loc.includes(window.location.origin) && (loc.includes('gcal=connected') || loc.includes('gcal_error='))) {
            popup.close();
            clearInterval(checkPopup);
            await loadGcalConnection();
            setGcalConnecting(false);
            const reason = new URL(loc).searchParams.get('gcal_error');
            if (reason) toast.error(gcalConnectError(reason));
            else if (loc.includes('gcal=connected')) toast.success('Google Calendar connected');
          }
        } catch (_) { /* cross-origin expected */ }
      }, 500);
      setTimeout(() => { clearInterval(checkPopup); if (!popup.closed) popup.close(); setGcalConnecting(false); }, 300000);
    } catch (err) {
      console.error('Error connecting Google Calendar:', err);
      toast.error('Could not start the Google Calendar connection. Try again.');
      setGcalConnecting(false);
    }
  };

  const handleDisconnectGcal = async () => {
    if (!gcalSyncId) return;
    setGcalDisconnecting(true);
    try {
      await supabase.from('google_calendar_sync').update({ is_active: false }).eq('id', gcalSyncId);
      await loadGcalConnection();
    } catch (err) {
      console.error('Error disconnecting Google Calendar:', err);
    } finally {
      setGcalDisconnecting(false);
    }
  };

  // Google Drive handlers
  const loadGdriveConnection = useCallback(async () => {
    if (!businessId || !supabase) return;
    const { data, error } = await supabase
      .from('google_drive_connections')
      .select('id, google_email, is_active, needs_reauth')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error && data) {
      setGdriveConnected(true);
      setGdriveEmail(data.google_email);
      setGdriveConnectionId(data.id);
      setGdriveNeedsReauth(!!data.needs_reauth);
    } else {
      setGdriveConnected(false);
      setGdriveEmail(null);
      setGdriveConnectionId(null);
      setGdriveNeedsReauth(false);
    }
  }, [businessId, supabase]);

  useEffect(() => { loadGdriveConnection(); }, [loadGdriveConnection]);

  // Open the Drive modal (and show the reason) when Google sends the owner back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const marker = params.get('gdrive');
    const err = params.get('error');
    if (marker !== 'connected' && err !== 'gdrive_scope_missing') return;
    if (err === 'gdrive_scope_missing') {
      setGdriveError('Google Drive access was not granted. Tick the Drive permission on the Google screen and try again.');
    }
    setShowGdriveSettings(true);
    params.delete('gdrive');
    if (err === 'gdrive_scope_missing') params.delete('error');
    const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', next);
  }, []);

  const handleConnectGdrive = async () => {
    if (!businessId) return;
    setGdriveConnecting(true);
    setGdriveError(null);
    try {
      const { data, error } = await supabase.functions.invoke('gdrive-oauth-init', {
        body: {
          businessId,
          returnUrl: `${window.location.origin}/app-store?tab=integrations&gdrive=connected`,
        },
      });
      if (error) throw error;
      const authUrl = data?.authUrl;
      if (!authUrl) throw new Error(data?.error || 'No auth URL returned');

      const popup = window.open(authUrl, 'gdrive-oauth', 'width=600,height=700,left=200,top=100');
      if (!popup) {
        window.location.assign(authUrl);
        return;
      }
      const checkPopup = setInterval(async () => {
        if (popup.closed) {
          clearInterval(checkPopup);
          await loadGdriveConnection();
          setGdriveConnecting(false);
          return;
        }
        try {
          const loc = popup.location.href;
          if (loc.includes(window.location.origin) && (loc.includes('gdrive=connected') || loc.includes('error=gdrive'))) {
            if (loc.includes('error=gdrive_scope_missing')) {
              setGdriveError('Google Drive access was not granted. Tick the Drive permission on the Google screen and try again.');
            }
            popup.close();
            clearInterval(checkPopup);
            await loadGdriveConnection();
            setGdriveConnecting(false);
          }
        } catch (_) { /* cross-origin expected */ }
      }, 500);
      setTimeout(() => { clearInterval(checkPopup); if (!popup.closed) popup.close(); setGdriveConnecting(false); }, 300000);
    } catch (err) {
      console.error('Error connecting Google Drive:', err);
      setGdriveError(err instanceof Error ? err.message : 'Could not start the Google sign-in.');
      setGdriveConnecting(false);
    }
  };

  const handleDisconnectGdrive = async () => {
    if (!gdriveConnectionId) return;
    setGdriveDisconnecting(true);
    try {
      await supabase.from('google_drive_connections').update({ is_active: false }).eq('id', gdriveConnectionId).select('id');
      await loadGdriveConnection();
    } catch (err) {
      console.error('Error disconnecting Google Drive:', err);
    } finally {
      setGdriveDisconnecting(false);
    }
  };

  const loadSearchConsoleConnection = useCallback(async () => {
    if (!businessId || !supabase) return;
    const { data, error } = await supabase
      .from('search_console_connections')
      .select('id, google_email, is_active, needs_reauth, last_synced_at, sync_error')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error && data) {
      setGscConnected(true);
      setGscEmail(data.google_email);
      setGscConnectionId(data.id);
      setGscLastSyncedAt(data.last_synced_at);
      setGscSyncError(data.sync_error);
      setGscNeedsReauth(!!data.needs_reauth);
    } else {
      setGscConnected(false);
      setGscEmail(null);
      setGscConnectionId(null);
      setGscLastSyncedAt(null);
      setGscSyncError(null);
      setGscNeedsReauth(false);
    }
  }, [businessId, supabase]);

  useEffect(() => { loadSearchConsoleConnection(); }, [loadSearchConsoleConnection]);

  const handleConnectSearchConsole = async () => {
    if (!businessId) return;
    setGscConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('search-console-oauth-init', {
        body: {
          businessId,
          returnUrl: `${window.location.origin}/app-store?tab=integrations&gsc=connected`,
        },
      });
      if (error) throw error;
      const authUrl = data?.authUrl;
      if (!authUrl) throw new Error('No auth URL returned');

      const popup = window.open(authUrl, 'gsc-oauth', 'width=600,height=700,left=200,top=100');
      const checkPopup = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          await loadSearchConsoleConnection();
          setGscConnecting(false);
          return;
        }
        try {
          const loc = popup.location.href;
          if (loc.includes(window.location.origin) && loc.includes('gsc=connected')) {
            popup.close();
            clearInterval(checkPopup);
            await loadSearchConsoleConnection();
            setGscConnecting(false);
          }
        } catch (_) { /* cross-origin expected */ }
      }, 500);
      setTimeout(() => { clearInterval(checkPopup); if (popup && !popup.closed) popup.close(); setGscConnecting(false); }, 300000);
    } catch (err) {
      console.error('Error connecting Search Console:', err);
      setGscConnecting(false);
    }
  };

  const handleDisconnectSearchConsole = async () => {
    if (!gscConnectionId) return;
    setGscDisconnecting(true);
    try {
      await supabase.from('search_console_connections').update({ is_active: false }).eq('id', gscConnectionId).select('id');
      await loadSearchConsoleConnection();
    } catch (err) {
      console.error('Error disconnecting Search Console:', err);
    } finally {
      setGscDisconnecting(false);
    }
  };

  // ── SendGrid verified sender (email without Gmail) ──────────
  const loadSendgridSenders = useCallback(async () => {
    if (!businessId) return;
    setSendgridLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_marketing_settings')
        .select('verified_senders')
        .eq('business_id', businessId)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      setSendgridSenders(Array.isArray(data?.verified_senders) ? data.verified_senders : []);
    } catch (err) {
      console.error('Error loading SendGrid senders:', err);
      setSendgridSenders([]);
    } finally {
      setSendgridLoading(false);
    }
  }, [businessId, supabase]);

  useEffect(() => { loadSendgridSenders(); }, [loadSendgridSenders]);
  useEffect(() => { if (showSendgridSettings) loadSendgridSenders(); }, [showSendgridSettings, loadSendgridSenders]);

  /** Non-2xx edge responses hide the real cause in error.context — dig it out. */
  const functionErrorMessage = async (error: unknown): Promise<string | null> => {
    const ctx = (error as { context?: Response } | null)?.context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const body = await ctx.json();
        if (body?.error) return String(body.error);
      } catch { /* not JSON */ }
    }
    return error instanceof Error ? error.message : null;
  };

  const handleAddSendgridEmail = async (email: string) => {
    if (!email || !businessId) return;
    setSendgridVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-sendgrid-email', {
        body: { email, businessId },
      });
      const parsed = data && typeof data === 'object'
        ? (data as { success?: boolean; error?: string; warning?: string; message?: string })
        : null;
      if (!parsed?.success) {
        throw new Error(parsed?.error || (error ? await functionErrorMessage(error) : null) || 'Email verification request failed');
      }
      toast.success(`Verification email sent to ${email} — click the link in that email to finish.`, { duration: 8000 });
      await loadSendgridSenders();
    } catch (err) {
      console.error('Error verifying SendGrid email:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to send verification email');
    } finally {
      setSendgridVerifying(false);
    }
  };

  const handleCheckSendgridStatus = async (sender: SendgridSender) => {
    if (!businessId || !sender.sendgrid_sender_id) return;
    setSendgridCheckingId(sender.sendgrid_sender_id);
    try {
      const { data, error } = await supabase.functions.invoke('check-sendgrid-verification', {
        body: { businessId, senderId: sender.sendgrid_sender_id, email: sender.email },
      });
      if (error) throw new Error((error as Error).message || 'Failed to check verification status');
      if (data?.success) {
        if (data.verified) toast.success(`${sender.email} is verified and ready to send.`);
        else toast(`Still pending — check your inbox for the verification link.`, { icon: '⏳' });
        await loadSendgridSenders();
      } else {
        throw new Error(data?.error || 'Failed to check verification status');
      }
    } catch (err) {
      console.error('Error checking SendGrid verification:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to check verification status');
    } finally {
      setSendgridCheckingId(null);
    }
  };

  const handleRemoveSendgridSender = async (sender: SendgridSender) => {
    if (!businessId) return;
    if (!confirm(`Remove ${sender.email}? You'll need to verify a new address to send email without Gmail.`)) return;
    try {
      const updated = sendgridSenders.filter((s) => s.email !== sender.email);
      const { error } = await supabase
        .from('email_marketing_settings')
        .update({ verified_senders: updated })
        .eq('business_id', businessId);
      if (error) throw error;
      await loadSendgridSenders();
    } catch (err) {
      console.error('Error removing SendGrid sender:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to remove sender');
    }
  };

  const sendgridVerified = sendgridSenders.some((s) => s.verification_status === 'verified');

  const handleGmailClick = () => {
    setShowGmailSettings(true);
  };

  const handleTwilioClick = () => {
    // While a Nexus number is active the Twilio columns hold the platform
    // subaccount — manage it from the Nexus card instead of exposing credentials.
    if (usingNexusNumber) {
      setShowNexusPhone(true);
      return;
    }
    setShowTwilioSettings(true);
  };

  const maskToken = (token: string) => {
    if (!token || token.length < 8) return token;
    return token.slice(0, 4) + '•'.repeat(token.length - 8) + token.slice(-4);
  };

  const startEditing = (field: string) => {
    if (!twilioSettings) return;
    
    switch (field) {
      case 'accountSid':
        setTempAccountSid(twilioSettings.twilio_account_sid || '');
        setEditingAccountSid(true);
        break;
      case 'authToken':
        setTempAuthToken(twilioSettings.twilio_auth_token || '');
        setEditingAuthToken(true);
        break;
      case 'phoneNumber':
        setTempPhoneNumber(twilioSettings.VITE_TWILIO_PHONE_NUMBER || '');
        setEditingPhoneNumber(true);
        break;
      case 'callForwarding':
        setTempCallForwardingEnabled(twilioSettings.twilio_call_forwarding_enabled || false);
        setTempForwardingNumber(twilioSettings.twilio_forwarding_number || '');
        setEditingCallForwarding(true);
        break;
    }
  };

  const cancelEditing = (field: string) => {
    switch (field) {
      case 'accountSid':
        setEditingAccountSid(false);
        setTempAccountSid('');
        break;
      case 'authToken':
        setEditingAuthToken(false);
        setTempAuthToken('');
        break;
      case 'phoneNumber':
        setEditingPhoneNumber(false);
        setTempPhoneNumber('');
        break;
      case 'callForwarding':
        setEditingCallForwarding(false);
        setTempCallForwardingEnabled(false);
        setTempForwardingNumber('');
        break;
    }
  };

  const saveTwilioField = async (field: string) => {
    if (!businessId || !twilioSettings) return;
    
    setIsSaving(true);
    try {
      let updateData: any = {};
      
      switch (field) {
        case 'accountSid':
          updateData.twilio_account_sid = tempAccountSid;
          setEditingAccountSid(false);
          break;
        case 'authToken':
          updateData.twilio_auth_token = tempAuthToken;
          setEditingAuthToken(false);
          break;
        case 'phoneNumber':
          updateData.VITE_TWILIO_PHONE_NUMBER = tempPhoneNumber;
          setEditingPhoneNumber(false);
          break;
        case 'callForwarding':
          updateData.twilio_call_forwarding_enabled = tempCallForwardingEnabled;
          updateData.twilio_forwarding_number = tempCallForwardingEnabled ? tempForwardingNumber : null;
          setEditingCallForwarding(false);
          break;
      }

      const { error } = await supabase
        .from('businesses')
        .update(updateData)
        .eq('id', businessId);

      if (error) throw error;

      setTwilioSettings({ ...twilioSettings, ...updateData });
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving Twilio field:', error);
      setIsSaving(false);
    }
  };

  const handleActivateTwilio = async () => {
    if (!businessId || !twilioSettings) return;
    
    setIsSaving(true);
    setIsConfiguringWebhooks(true);
    
    try {
      // Validate required fields
      if (!twilioSettings.twilio_account_sid || !twilioSettings.twilio_auth_token || !twilioSettings.VITE_TWILIO_PHONE_NUMBER) {
        throw new Error('Please fill in all required fields: Account SID, Auth Token, and Phone Number');
      }

      // Configure webhooks
      const webhookResult = await configureWebhooksDirectly(
        twilioSettings.twilio_account_sid,
        twilioSettings.twilio_auth_token,
        twilioSettings.VITE_TWILIO_PHONE_NUMBER
      );

      // Update business record with all necessary fields
      const updateData: any = {
        twilio_enabled: true,
        twilio_status: 'active',
        twilio_webhooks_configured: true,
        twilio_webhook_configured_at: new Date().toISOString()
      };

      // Add Application SID if it was created successfully
      if (webhookResult.applicationSid) {
        updateData.twilio_application_sid = webhookResult.applicationSid;
        console.log('✅ TwiML Application SID saved to database:', webhookResult.applicationSid);
      }

      const { error } = await supabase
        .from('businesses')
        .update(updateData)
        .eq('id', businessId);

      if (error) throw error;

      setTwilioSettings({ 
        ...twilioSettings, 
        twilio_enabled: true,
        twilio_status: 'active',
        twilio_webhooks_configured: true,
        twilio_application_sid: webhookResult.applicationSid
      });
      setTwilioConnected(true);
      setIsConfiguringWebhooks(false);
      setIsSaving(false);
    } catch (error: any) {
      console.error('Error activating Twilio:', error);
      // Show error message with better formatting
      const errorMessage = error.message || 'Unknown error occurred';
      // Replace \n with actual line breaks for better readability
      const formattedMessage = errorMessage.replace(/\n/g, '\n');
      alert(`Failed to activate Twilio:\n\n${formattedMessage}`);
      setIsConfiguringWebhooks(false);
      setIsSaving(false);
    }
  };

  const handleDisconnectTwilio = async () => {
    if (!businessId) return;
    
    if (!confirm('Are you sure you want to disconnect Twilio? This will disable SMS and voice communications.')) {
      return;
    }
    
    setIsDisconnecting(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ 
          twilio_enabled: false,
          twilio_status: 'inactive'
        })
        .eq('id', businessId);

      if (error) throw error;

      await disconnectTwilio({ businessId });
      
      setTwilioSettings({ 
        ...twilioSettings, 
        twilio_enabled: false,
        twilio_status: 'inactive'
      });
      setTwilioConnected(false);
      setIsDisconnecting(false);
    } catch (error) {
      console.error('Error disconnecting Twilio:', error);
      setIsDisconnecting(false);
    }
  };

  // Stripe — sync status from business context
  useEffect(() => {
    if (business) {
      setStripeConnectStatus({
        isConnected: !!business.stripe_connect_account_id,
        accountId: business.stripe_connect_account_id,
        onboardingCompleted: business.stripe_connect_onboarding_completed,
        chargesEnabled: business.stripe_connect_charges_enabled,
      });
    }
  }, [business]);

  // Handle Stripe Connect return URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeConnectParam = urlParams.get('stripe_connect');
    if (!stripeConnectParam || !business?.id) return;

    const checkStatus = async () => {
      try {
        await supabase.functions.invoke('check-stripe-connect-status', {
          body: { businessId: business.id }
        });
      } catch { /* status check is best-effort */ }
      forceLoadBusinesses();
      const params = new URLSearchParams(window.location.search);
      params.delete('stripe_connect');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState({}, '', next);
    };

    setTimeout(checkStatus, stripeConnectParam === 'success' ? 2000 : 1000);
  }, [business?.id, forceLoadBusinesses, supabase]);

  const handleConnectStripe = async () => {
    if (!business) return;
    setStripeLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-stripe-connect-account', {
        body: {
          businessId: business.id,
          businessEmail: business.email || '',
          businessName: business.name || '',
          // getSiteUrl(): native (Capacitor) origins like capacitor://localhost
          // are rejected by Stripe account links as "Invalid URL".
          returnUrl: `${getSiteUrl()}/app-store?tab=integrations&stripe_connect=success`,
          refreshUrl: `${getSiteUrl()}/app-store?tab=integrations&stripe_connect=refresh`,
        }
      });
      if (error) {
        // Non-2xx responses hide the real message inside error.context.
        let detail = '';
        try {
          detail = (await (error as { context?: Response }).context?.json())?.error || '';
        } catch { /* keep generic */ }
        throw new Error(detail || error.message);
      }
      if (data?.error) throw new Error(data.error);
      if (!data?.onboardingUrl) throw new Error('Stripe did not return an onboarding link');
      window.location.href = data.onboardingUrl;
    } catch (error) {
      console.error('Error connecting to Stripe:', error);
      const message = error instanceof Error ? error.message : 'Please try again.';
      alert(`Failed to connect to Stripe: ${message}`);
    } finally {
      setStripeLoading(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!business || !stripeConnectStatus.isConnected) return;
    if (!confirm('Are you sure you want to disconnect your Stripe account? This will disable payment processing.')) return;

    setStripeLoading(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          stripe_connect_account_id: null,
          stripe_connect_onboarding_completed: false,
          stripe_connect_charges_enabled: false,
        })
        .eq('id', business.id);
      if (error) throw error;

      setStripeConnectStatus({ isConnected: false, accountId: undefined, onboardingCompleted: false, chargesEnabled: false });
      forceLoadBusinesses();
      setShowStripeSettings(false);
    } catch (error) {
      console.error('Error disconnecting from Stripe:', error);
      alert('Failed to disconnect from Stripe. Please try again.');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleRefreshStripeStatus = async () => {
    if (!business || !stripeConnectStatus.isConnected) return;
    setStripeLoading(true);
    try {
      const { error } = await supabase.functions.invoke('check-stripe-connect-status', {
        body: { businessId: business.id }
      });
      if (error) throw error;
      forceLoadBusinesses();
      alert('Stripe account status refreshed successfully!');
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      alert('Failed to check Stripe status. Please try again.');
    } finally {
      setStripeLoading(false);
    }
  };

  const stripeConnected = stripeConnectStatus.isConnected && stripeConnectStatus.onboardingCompleted && stripeConnectStatus.chargesEnabled;

  const nexusConnectGrid = (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
      <NexusConnectIntegrationCard
        onClick={handleGmailClick}
        iconSrc="/Gmailicon.png"
        title="Gmail"
        category="Communication"
        description="Email integration and automation"
        status={<IntegrationStatus connected={gmailConnected} />}
      />
      <NexusConnectIntegrationCard
        onClick={() => setShowNexusPhone(true)}
        iconSrc="/Messagesappicon.png"
        title="Nexus Phone Number"
        category="Communication"
        description={nexusPhoneActive ? formatNexusPhone(nexusNumber?.phone_number) : 'Get a business number in one click'}
        status={
          nexusPhoneActive ? (
            <IntegrationStatus connected />
          ) : nexusNumber?.status === 'suspended' ? (
            <span className="text-xs text-amber-600 font-medium">{nexusNumber.suspended_reason === 'no_funds' ? 'Paused — add funds' : 'Paused'}</span>
          ) : (
            <IntegrationStatus connected={false} label="Get a number" />
          )
        }
      />
      <NexusConnectIntegrationCard
        onClick={handleTwilioClick}
        iconSrc="/twilio.png"
        title="Twilio"
        category="Communication"
        description={usingNexusNumber ? 'Using your Nexus phone number' : 'SMS and voice communications'}
        status={
          usingNexusNumber ? (
            <span className="text-xs text-gray-400 font-medium">Not needed</span>
          ) : (
            <IntegrationStatus connected={ownTwilioConnected} />
          )
        }
      />
      <NexusConnectIntegrationCard
        onClick={() => setShowSendgridSettings(true)}
        iconSrc={PLATFORM_ICON_SRC.sendgrid}
        framed
        title="SendGrid Email"
        category="Communication"
        description="Send email without Gmail via a verified sender"
        status={
          sendgridVerified ? (
            <IntegrationStatus connected />
          ) : sendgridSenders.length > 0 ? (
            <span className="text-xs text-amber-600 font-medium">Pending verification</span>
          ) : (
            <IntegrationStatus connected={false} />
          )
        }
      />
      <NexusConnectIntegrationCard
        onClick={() => setShowStripeSettings(true)}
        iconSrc="/stripeicon.png"
        title="Stripe"
        category="Payments"
        description="Accept payments and process invoices"
        status={
          stripeConnected ? (
            <IntegrationStatus connected />
          ) : stripeConnectStatus.isConnected && !stripeConnectStatus.onboardingCompleted ? (
            <span className="text-xs text-amber-600 font-medium">Setup incomplete</span>
          ) : (
            <IntegrationStatus connected={false} />
          )
        }
      />
      {GBP_ENABLED && (
        <NexusConnectIntegrationCard
          onClick={() => setShowGbpSettings(true)}
          iconSrc={PLATFORM_ICON_SRC.google_business}
          framed
          title="Google Business Profile"
          category="Google"
          description="Connect your profile for reviews, posts, and photos"
          status={<IntegrationStatus connected={gbpConnected} label="Open" />}
        />
      )}
      {SOCIAL_ENABLED && (
        <>
          <NexusConnectIntegrationCard
            onClick={() => setShowSocialSettings('instagram')}
            iconSrc={PLATFORM_ICON_SRC.instagram}
            framed
            title="Instagram"
            category="Meta"
            description="Connect Instagram for automated posts"
            status={<IntegrationStatus connected={instagramConnected} label="Open" />}
          />
          <NexusConnectIntegrationCard
            onClick={() => setShowSocialSettings('facebook')}
            iconSrc={PLATFORM_ICON_SRC.facebook}
            framed
            title="Facebook"
            category="Meta"
            description="Connect a Facebook Page for automated posts"
            status={<IntegrationStatus connected={facebookConnected} label="Open" />}
          />
          <NexusConnectIntegrationCard
            onClick={() => setShowSocialSettings('linkedin')}
            iconSrc={PLATFORM_ICON_SRC.linkedin}
            framed
            title="LinkedIn"
            category="LinkedIn"
            description="Connect a profile or company page"
            status={<IntegrationStatus connected={linkedinConnected} label="Open" />}
          />
        </>
      )}
      <NexusConnectIntegrationCard
        onClick={() => setShowGcalSettings(true)}
        iconSrc="/Gcalnder.png"
        title="Google Calendar"
        category="Google"
        description="Two-way sync bookings"
        status={gcalNeedsReauth
          ? <span className="text-xs text-amber-600 font-medium">Reconnect</span>
          : <IntegrationStatus connected={gcalConnected} />}
      />
      <NexusConnectIntegrationCard
        onClick={() => setShowGdriveSettings(true)}
        iconSrc="/google-drive.svg"
        title="Google Drive"
        category="Google"
        description="Files for your AI team"
        status={<IntegrationStatus connected={gdriveConnected} />}
      />
      <NexusConnectIntegrationCard
        onClick={() => setShowGscSettings(true)}
        iconSrc="/search-console.svg"
        title="Search Console"
        category="Google"
        description="Sync search performance and sitemaps"
        status={<IntegrationStatus connected={gscConnected} />}
      />
    </div>
  );

  return (
    <>
      {variant === 'nexusConnect' ? (
        nexusConnectGrid
      ) : (
    <div className="flex-1 overflow-auto bg-gray-50 min-h-screen" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'}}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-5 md:py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-500 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            <span className="text-sm font-medium">Back to Settings</span>
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Integrations</h1>
          <p className="text-sm text-gray-500 mt-1">Connect and manage third-party services</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-5">
        {/* ─── COMMUNICATION ─── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 md:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Communication</h3>
            <p className="text-xs text-gray-500 mb-4">Email and SMS integrations</p>

            <div className="space-y-1">
              <button
                onClick={handleGmailClick}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src="/Gmailicon.png" alt="" className="w-10 h-10 rounded-xl shadow-sm flex-shrink-0 object-contain" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Gmail</p>
                  <p className="text-xs text-gray-500">Email integration and automation</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {gmailConnected && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>

              <button
                onClick={() => setShowNexusPhone(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src="/Messagesappicon.png" alt="" className="w-10 h-10 rounded-xl shadow-sm flex-shrink-0 object-contain" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Nexus Phone Number</p>
                  <p className="text-xs text-gray-500">
                    {nexusPhoneActive
                      ? `${formatNexusPhone(nexusNumber?.phone_number)} · texting & calling, billed by Nexus`
                      : 'Get a business number for texting & calling in one click'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {nexusPhoneActive ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Active
                    </span>
                  ) : nexusNumber?.status === 'suspended' ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                      {nexusNumber.suspended_reason === 'no_funds' ? 'Paused — add funds' : 'Paused'}
                    </span>
                  ) : null}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>

              <button
                onClick={handleTwilioClick}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src="/twilio.png" alt="" className="w-10 h-10 rounded-xl shadow-sm flex-shrink-0 object-contain" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Twilio</p>
                  <p className="text-xs text-gray-500">
                    {usingNexusNumber ? 'Not needed — you\'re using your Nexus phone number' : 'SMS and voice communications'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {ownTwilioConnected && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>

              <button
                onClick={() => setShowSendgridSettings(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src={PLATFORM_ICON_SRC.sendgrid} alt="" className="w-10 h-10 rounded-xl shadow-sm border border-gray-100 bg-white flex-shrink-0 object-contain" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">SendGrid Email</p>
                  <p className="text-xs text-gray-500">Send email without Gmail via a verified sender</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {sendgridVerified ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  ) : sendgridSenders.length > 0 ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                      Pending
                    </span>
                  ) : null}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* ─── PAYMENTS ─── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 md:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Payments</h3>
            <p className="text-xs text-gray-500 mb-4">Payment processing and invoicing</p>

            <div className="space-y-1">
              <button
                onClick={() => setShowStripeSettings(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src="/stripeicon.png" alt="" className="w-10 h-10 rounded-xl shadow-sm flex-shrink-0 object-contain" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Stripe</p>
                  <p className="text-xs text-gray-500">Accept payments and process invoices</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {stripeConnected ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  ) : stripeConnectStatus.isConnected && !stripeConnectStatus.onboardingCompleted ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                      Setup Incomplete
                    </span>
                  ) : null}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* ─── GOOGLE SERVICES ─── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 md:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Google Services</h3>
            <p className="text-xs text-gray-500 mb-4">Calendar, Search Console, and business profile</p>

            <div className="space-y-1">
              {GBP_ENABLED && (
              <button
                onClick={() => setShowGbpSettings(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src={PLATFORM_ICON_SRC.google_business} alt="" className="w-10 h-10 rounded-xl shadow-sm border border-gray-100 flex-shrink-0 object-contain bg-white" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Google Business Profile</p>
                  <p className="text-xs text-gray-500">Connect your profile for reviews, posts, and photos</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {gbpConnected && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>
              )}

              <button
                onClick={() => setShowGcalSettings(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src="/Gcalnder.png" alt="" className="w-10 h-10 rounded-xl shadow-sm flex-shrink-0 object-contain" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Google Calendar</p>
                  <p className="text-xs text-gray-500">Two-way sync bookings</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {gcalNeedsReauth ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                      Reconnect
                    </span>
                  ) : gcalConnected ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  ) : null}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>

              <button
                onClick={() => setShowGdriveSettings(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src="/google-drive.svg" alt="" className="w-10 h-10 rounded-xl shadow-sm border border-gray-100 bg-white p-1.5 flex-shrink-0 object-contain" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Google Drive</p>
                  <p className="text-xs text-gray-500">Files for your AI team</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {gdriveConnected && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${gdriveNeedsReauth ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${gdriveNeedsReauth ? 'bg-amber-500' : 'bg-green-500'}`} />
                      {gdriveNeedsReauth ? 'Reconnect' : 'Connected'}
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>

              <button
                onClick={() => setShowGscSettings(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src="/search-console.svg" alt="" className="w-10 h-10 rounded-xl shadow-sm border border-gray-100 bg-white flex-shrink-0 object-contain" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Search Console</p>
                  <p className="text-xs text-gray-500">Sync search performance and sitemaps</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {gscConnected && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>
            </div>
          </div>
        </div>

        {SOCIAL_ENABLED && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 md:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Social Media</h3>
            <p className="text-xs text-gray-500 mb-4">Facebook, Instagram, and LinkedIn via Ayrshare</p>
            <div className="space-y-1">
              <button
                onClick={() => setShowSocialSettings('instagram')}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src={PLATFORM_ICON_SRC.instagram} alt="" className="w-10 h-10 rounded-xl shadow-sm border border-gray-100 flex-shrink-0 object-contain bg-white" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Instagram</p>
                  <p className="text-xs text-gray-500">Connect Instagram for automated posts</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {instagramConnected && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>
              <button
                onClick={() => setShowSocialSettings('facebook')}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src={PLATFORM_ICON_SRC.facebook} alt="" className="w-10 h-10 rounded-xl shadow-sm border border-gray-100 flex-shrink-0 object-contain bg-white" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Facebook</p>
                  <p className="text-xs text-gray-500">Connect a Facebook Page for automated posts</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {facebookConnected && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>
              <button
                onClick={() => setShowSocialSettings('linkedin')}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group text-left"
              >
                <img src={PLATFORM_ICON_SRC.linkedin} alt="" className="w-10 h-10 rounded-xl shadow-sm border border-gray-100 flex-shrink-0 object-contain bg-white" draggable={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">LinkedIn</p>
                  <p className="text-xs text-gray-500">Connect a profile or company page</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {linkedinConnected && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                      Connected
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>
            </div>
          </div>
        </div>
        )}

      </div>
    </div>
      )}

      {/* Modals */}
      <GmailSettingsModal 
        isOpen={showGmailSettings} 
        onClose={() => setShowGmailSettings(false)} 
        gmailConnected={gmailConnected}
        gmailConnections={gmailConnections}
        handleConnectGmail={handleConnectGmail}
        handleDisconnectGmail={handleDisconnectGmail}
        isConnecting={isConnecting}
      />
      {/* Shared Twilio settings — supports both the manual method and Twilio Connect */}
      <TwilioSettings
        isOpen={showTwilioSettings}
        onClose={() => setShowTwilioSettings(false)}
        onSave={() => void loadTwilioSettings()}
      />
      {/* Fully in-app phone number (platform-owned Twilio subaccount, billed by Nexus) */}
      <NexusPhoneNumberModal
        isOpen={showNexusPhone}
        onClose={() => setShowNexusPhone(false)}
        onChanged={() => void loadTwilioSettings()}
        onOpenTwilioSettings={() => {
          setShowNexusPhone(false);
          setShowTwilioSettings(true);
        }}
      />
      <StripeSettingsModal
        isOpen={showStripeSettings}
        onClose={() => setShowStripeSettings(false)}
        connectStatus={stripeConnectStatus}
        loading={stripeLoading}
        onConnect={handleConnectStripe}
        onDisconnect={handleDisconnectStripe}
        onRefreshStatus={handleRefreshStripeStatus}
        businessId={businessId}
        supabase={supabase}
      />
      <GcalSettingsModal
        isOpen={showGcalSettings}
        onClose={() => setShowGcalSettings(false)}
        connected={gcalConnected}
        needsReauth={gcalNeedsReauth}
        email={gcalEmail}
        onConnect={handleConnectGcal}
        onDisconnect={handleDisconnectGcal}
        isConnecting={gcalConnecting}
        isDisconnecting={gcalDisconnecting}
        businessId={businessId}
        calendarSyncId={gcalSyncId}
        businessSlug={business?.slug || ''}
        businessName={business?.name || ''}
        timezone={business?.timezone || 'America/Vancouver'}
        businessHours={business?.business_hours}
        logoUrl={business?.booking_settings?.logo_url}
        supabase={supabase}
      />
      <GdriveSettingsModal
        isOpen={showGdriveSettings}
        onClose={() => { setShowGdriveSettings(false); setGdriveError(null); }}
        connected={gdriveConnected}
        email={gdriveEmail}
        needsReauth={gdriveNeedsReauth}
        error={gdriveError}
        onConnect={handleConnectGdrive}
        onDisconnect={handleDisconnectGdrive}
        isConnecting={gdriveConnecting}
        isDisconnecting={gdriveDisconnecting}
      />
      <SearchConsoleSettingsModal
        isOpen={showGscSettings}
        onClose={() => setShowGscSettings(false)}
        connected={gscConnected}
        email={gscEmail}
        lastSyncedAt={gscLastSyncedAt}
        syncError={gscSyncError}
        needsReauth={gscNeedsReauth}
        onConnect={handleConnectSearchConsole}
        onDisconnect={handleDisconnectSearchConsole}
        isConnecting={gscConnecting}
        isDisconnecting={gscDisconnecting}
      />
      <GbpSettingsModal
        isOpen={GBP_ENABLED && showGbpSettings}
        onClose={() => setShowGbpSettings(false)}
        onConnectionChange={setGbpConnected}
      />
      {SOCIAL_ENABLED && showSocialSettings && (
        <SocialSettingsModal
          isOpen
          platform={showSocialSettings}
          onClose={() => setShowSocialSettings(null)}
          onConnectionChange={() => {
            void (async () => {
              if (!businessId) return;
              const { data } = await supabase
                .from('social_media_connections')
                .select('platform')
                .eq('business_id', businessId)
                .eq('provider', 'ayrshare')
                .eq('is_active', true);
              const platforms = new Set((data || []).map((r) => r.platform as string));
              setFacebookConnected(platforms.has('facebook'));
              setInstagramConnected(platforms.has('instagram'));
              setLinkedinConnected(platforms.has('linkedin'));
            })();
          }}
        />
      )}
      <SendgridSettingsModal
        isOpen={showSendgridSettings}
        onClose={() => setShowSendgridSettings(false)}
        senders={sendgridSenders}
        loading={sendgridLoading}
        verifying={sendgridVerifying}
        checkingId={sendgridCheckingId}
        onAddEmail={handleAddSendgridEmail}
        onCheckStatus={handleCheckSendgridStatus}
        onRemove={handleRemoveSendgridSender}
      />
    </>
  );
}